/**
 * Génération de l'avatar via la librairie open source DiceBear (style
 * "Notionists Neutral" — CC0, sans attribution requise). Rendu 100% local :
 * `createAvatar()` produit une chaîne SVG déterministe à partir d'options
 * explicites, aucun appel réseau à l'exécution (contrairement à l'API HTTP
 * publique de DiceBear). 5 catégories de traits (sourcils, yeux, lunettes,
 * bouche, nez) + une couleur de fond.
 */
import { createAvatar } from "@dicebear/core";
import * as notionistsNeutral from "@dicebear/notionists-neutral";

const { schema } = notionistsNeutral;

// Clé globale (non préfixée langue, voir LanguageManager.GLOBAL_KEY_SUFFIXES) —
// un avatar ne dépend pas de la langue courante. Partagée par AccountSync,
// AvatarPage, AccountMenu et Profile pour lire/écrire toujours la même clé.
export const AVATAR_STORAGE_KEY = "geonerd.avatar";

export const AVATAR_COLORS = [
    "#1e90ff", // bleu (plus vif que le bleu de marque — celui-ci se fondait dans le fond de page)
    "#F05050", // rouge
    "#a855f7", // violet
    "#1dd710", // vert
    "#ffe300", // jaune
    "#ff8a3d", // orange
    "#00c2d1", // cyan
    "#ff5fa2", // rose
];

export const BROWS_OPTIONS = schema.properties.brows.items.enum;
export const EYES_OPTIONS = schema.properties.eyes.items.enum;
export const GLASSES_OPTIONS = ["none", ...schema.properties.glasses.items.enum];
export const MOUTH_OPTIONS = schema.properties.lips.items.enum;
export const NOSE_OPTIONS = schema.properties.nose.items.enum;

const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/;

function pick(arr) {
    return arr[Math.floor(Math.random() * arr.length)];
}

export function getDefaultAvatar() {
    return {
        brows: BROWS_OPTIONS[0],
        eyes: EYES_OPTIONS[0],
        glasses: "none",
        mouth: MOUTH_OPTIONS[0],
        nose: NOSE_OPTIONS[0],
        color: AVATAR_COLORS[0],
    };
}

export function randomAvatar() {
    // Lunettes présentes ~40% du temps — sinon elles dominent le random à chaque tirage.
    const hasGlasses = Math.random() < 0.4;
    return {
        brows: pick(BROWS_OPTIONS),
        eyes: pick(EYES_OPTIONS),
        glasses: hasGlasses ? pick(schema.properties.glasses.items.enum) : "none",
        mouth: pick(MOUTH_OPTIONS),
        nose: pick(NOSE_OPTIONS),
        color: pick(AVATAR_COLORS),
    };
}

/** Retombe sur les valeurs par défaut pour tout champ manquant/invalide (storage corrompu, ancien format). */
export function sanitizeAvatar(config) {
    const d = getDefaultAvatar();
    if (!config || typeof config !== "object") return d;
    return {
        brows: BROWS_OPTIONS.includes(config.brows) ? config.brows : d.brows,
        eyes: EYES_OPTIONS.includes(config.eyes) ? config.eyes : d.eyes,
        glasses: GLASSES_OPTIONS.includes(config.glasses) ? config.glasses : d.glasses,
        mouth: MOUTH_OPTIONS.includes(config.mouth) ? config.mouth : d.mouth,
        nose: NOSE_OPTIONS.includes(config.nose) ? config.nose : d.nose,
        color: HEX_COLOR_RE.test(config.color) ? config.color : d.color,
    };
}

/**
 * Avatar enregistré sur CET appareil, déjà validé. Un visiteur anonyme peut en
 * avoir un (la page /avatar ne demande pas de compte) : il est simplement
 * remonté au serveur à sa première connexion.
 */
export function loadStoredAvatar() {
    let config = null;
    try {
        config = JSON.parse(localStorage.getItem(AVATAR_STORAGE_KEY));
    } catch {
        /* pas d'avatar enregistré, ou storage corrompu */
    }
    return sanitizeAvatar(config);
}

export function renderAvatarSVG(config, size = 120) {
    const c = sanitizeAvatar(config);
    const hasGlasses = c.glasses !== "none";
    return createAvatar(notionistsNeutral, {
        size,
        brows: [c.brows],
        eyes: [c.eyes],
        lips: [c.mouth],
        nose: [c.nose],
        glasses: hasGlasses ? [c.glasses] : undefined,
        glassesProbability: hasGlasses ? 100 : 0,
        backgroundColor: [c.color.replace("#", "")],
        backgroundType: ["solid"],
    }).toString();
}
